/**
 * @file BloodGlucoseView.jsx
 * @author Daniel Aguirre
 * @description BloodGlucose View Page
 */

// Dependencies
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ReactComponent as Glucose } from '@nephro/assets/glucose-icon-dark.svg';
import AppCardLayout from '@nephro/components/AppCardLayout';

// Helpers
import { getMonthName } from 'commons/helpers/utils';
import {
  getGlycosylatedHemoglobinLevel,
  getPatientStage
} from '../helpers/utils';
import {
  BLOOD_GLUCOSE_INDICATOR,
  HIGH,
  STAGE_INDICATOR
} from '../helpers/constants';
import CardIndicator from '../components/CardIndicator';
import usePatientIndicators from '../helpers/usePatientIndicators';

function BloodGlucoseView() {
  const [stageIndicator] = usePatientIndicators(STAGE_INDICATOR);
  const [last, previous] = usePatientIndicators(BLOOD_GLUCOSE_INDICATOR);

  const { t: translate } = useTranslation();
  const [lastControl, setLastControl] = useState({
    value: 0,
    date: new Date(),
    level: ''
  });
  const [previousControl, setPreviousControl] = useState({
    value: 0,
    date: new Date(),
    level: ''
  });

  const calculateGraphicValues = () => {
    const stage = getPatientStage(stageIndicator?.valor);
    if (previous) {
      setPreviousControl({
        value: previous.valor,
        date: new Date(new Date(previous.fecha).getTime() + 5 * 60 * 60 * 1000),
        level: getGlycosylatedHemoglobinLevel(previous.valor, stage)
      });
    }

    if (last) {
      setLastControl({
        value: last.valor,
        date: new Date(new Date(last.fecha).getTime() + 5 * 60 * 60 * 1000),
        level: getGlycosylatedHemoglobinLevel(last.valor, stage)
      });
    }
  };

  useEffect(() => {
    calculateGraphicValues();
  }, [stageIndicator]);

  return (
    <AppCardLayout>
      <div
        style={{ fontSize: 18 }}
        className="text-center p-5 items-center m-auto text-baxterGray mt-1">
        <header style={{ marginBottom: 17 }} className="flex flex-wrap">
          <Glucose />
          <h1 style={{ fontSize: 20, marginLeft: 14 }}>
            <span style={{ verticalAlign: '-webkit-baseline-middle' }}>
              {translate('indicators.bloodGlucose.title')}
            </span>
          </h1>
        </header>
        <p className="text-left mb-8">
          {translate('indicators.bloodGlucose.description')}
        </p>
        <div className="w-full flex">
          <section className="flex flex-wrap mb-10 w-full justify-center gap-2 sm:gap-x-36">
            {last && (
              <CardIndicator
                title={translate('indicators.last')}
                date={`${translate(
                  getMonthName(lastControl.date.getMonth())
                )} ${lastControl.date.getFullYear()}`}
                value={lastControl ? lastControl.value : 0}
                level={translate(`indicators.${lastControl.level}`)}
                isHigh={lastControl.level === HIGH}
              />
            )}
            {previous && (
              <CardIndicator
                title={translate('indicators.previous')}
                date={`${translate(
                  getMonthName(previousControl.date.getMonth())
                )} ${previousControl.date.getFullYear()}`}
                value={previousControl ? previousControl.value : 0}
                level={translate(`indicators.${previousControl.level}`)}
                isHigh={previousControl.level === HIGH}
              />
            )}
          </section>
        </div>
      </div>
    </AppCardLayout>
  );
}

export default BloodGlucoseView;
